import { Money, compareDates, dec, localDate, type LocalDate } from "@despejo/domain";
import { type BusinessCalendar, type DeadlineRule, computeInstallments } from "@despejo/compliance-engine";
import { type EngineParameter, type UsedParameter, decimalField, pickParameter, usedParameter } from "./params.js";

/**
 * Pagos parciales del impuesto sobre las utilidades (plan §18,
 * docs/tax/PAGOS_PARCIALES.md).
 *
 *   base        = max(tax(Y-1), average of tax(Y-1..Y-3) over the periods actually filed)   (Ley 7092 art. 22 a)
 *   installment = share (75%) × base / 3                                                  (art. 22 b)
 *   due dates   = last business day of June, September, December (Reg. 43198-H Transitorio I.2),
 *                 from the PAGOS_PARCIALES INSTALLMENTS rule and the CR holiday calendar.
 *
 * The amount Hacienda puts on the Cuenta Integral Tributaria is authoritative
 * (RPT art. 16); this is the expected figure to reconcile against.
 */

export const PARTIAL_PAYMENTS_ENGINE_VERSION = "pagos-parciales@1.0.0";

export interface PriorYearTax {
  year: number;
  /** Tax determined in that year's return; null = no return filed for that year. */
  tax: string | null;
  /** Obligation / filing reference for drill-down. */
  ref?: string;
}

export interface PaidInstallment {
  key: string;
  amount: string;
  ref?: string;
}

export interface PartialPaymentsInput {
  fiscalYear: number;
  priorYears: PriorYearTax[];
  /** First year of activity or no prior return: the taxpayer's estimate (art. 22 a, "estimación fundada"). */
  estimate?: string | null;
  /** Agricultural-only taxpayers pay in a single installment (art. 22 c): no pagos parciales. */
  agriculturalOnly?: boolean;
  paid?: PaidInstallment[];
}

export interface Installment {
  key: string;
  nominal: LocalDate;
  due: LocalDate;
  amount: string;
  paid: string;
  pending: string;
}

export type PartialBasis = "PRIOR_YEAR" | "AVERAGE_3" | "ESTIMATE" | "NONE";

export interface PartialPaymentsResult {
  engine: string;
  fiscalYear: number;
  computable: boolean;
  provisional: boolean;
  provisionalReasons: string[];
  parameters: UsedParameter[];
  basis: PartialBasis;
  priorYearTax: string | null;
  averageTax: string | null;
  averageOver: number[];
  base: string;
  share: string;
  installments: Installment[];
  total: string;
  exceptions: { code: "PARAMETER_VERSION_MISSING" | "NO_PRIOR_RETURN" | "AGRICULTURAL"; message: string }[];
  trace: { bucket: "prior_tax"; ref: string; year: number; amount: string }[];
}

type InstallmentsRule = Extract<DeadlineRule, { type: "INSTALLMENTS" }>;

export function computePartialPayments(
  input: PartialPaymentsInput,
  params: readonly EngineParameter[],
  rule: InstallmentsRule,
  calendar: BusinessCalendar,
  ruleProvisional = false,
): PartialPaymentsResult {
  const reasons: string[] = [];
  const used: UsedParameter[] = [];
  const exceptions: PartialPaymentsResult["exceptions"] = [];
  const trace: PartialPaymentsResult["trace"] = [];
  const y = input.fiscalYear;
  const empty = (basis: PartialBasis): PartialPaymentsResult => ({
    engine: PARTIAL_PAYMENTS_ENGINE_VERSION,
    fiscalYear: y,
    computable: false,
    provisional: true,
    provisionalReasons: reasons,
    parameters: used,
    basis,
    priorYearTax: null,
    averageTax: null,
    averageOver: [],
    base: "0.00",
    share: "0",
    installments: [],
    total: "0.00",
    exceptions,
    trace,
  });

  if (input.agriculturalOnly) {
    exceptions.push({ code: "AGRICULTURAL", message: "Actividad agropecuaria exclusiva: paga en una sola cuota con la declaración (art. 22 c)" });
    return { ...empty("NONE"), computable: true, provisional: false };
  }

  const p = pickParameter(params, "PAGOS_PARCIALES_BASE", localDate(y, 1, 1));
  if (!p) {
    exceptions.push({ code: "PARAMETER_VERSION_MISSING", message: `No hay parámetro PAGOS_PARCIALES_BASE vigente para ${y}` });
    reasons.push("Falta el parámetro PAGOS_PARCIALES_BASE");
    return empty("NONE");
  }
  used.push(usedParameter(p));
  if (p.review_status !== "APPROVED") reasons.push("Parámetros en borrador (DRAFT) pendientes de confirmación");
  if (ruleProvisional) reasons.push("Fechas de vencimiento por confirmar");
  const share = decimalField(p.value, "share");
  const count = Number(p.value.installments);
  if (!Number.isInteger(count) || count < 1) throw new TypeError("PAGOS_PARCIALES_BASE.installments must be a positive integer");

  const filed = input.priorYears
    .filter((r) => r.year >= y - 3 && r.year <= y - 1 && r.tax !== null)
    .sort((a, b) => b.year - a.year);
  for (const r of filed) trace.push({ bucket: "prior_tax", ref: r.ref ?? `renta:${r.year}`, year: r.year, amount: Money.of(r.tax!).toString() });
  const prev = filed.find((r) => r.year === y - 1);

  let basis: PartialBasis;
  let base: Money;
  let average: Money | null = null;
  if (prev) {
    const prevTax = Money.of(prev.tax!);
    average = Money.sum(filed.map((r) => Money.of(r.tax!))).dividedBy(filed.length);
    basis = average.cmp(prevTax) > 0 ? "AVERAGE_3" : "PRIOR_YEAR";
    base = prevTax.max(average);
  } else if (input.estimate !== undefined && input.estimate !== null) {
    basis = "ESTIMATE";
    base = Money.of(input.estimate);
    reasons.push("Base estimada por el contribuyente (primer periodo o sin declaración anterior)");
    if (filed.length) {
      // Art. 22 a): use the returns that were filed; the estimate only replaces the missing year.
      average = Money.sum(filed.map((r) => Money.of(r.tax!))).dividedBy(filed.length);
      base = base.max(average);
    }
  } else {
    exceptions.push({ code: "NO_PRIOR_RETURN", message: `No hay declaración de renta ${y - 1}: se necesita una estimación fundada (art. 22 a)` });
    reasons.push(`Falta la declaración ${y - 1} o una estimación`);
    return empty("NONE");
  }
  if (base.isNegative()) base = Money.zero();

  const each = base.times(share).dividedBy(count).round(2);
  const dates = computeInstallments(rule, y, calendar);
  const paidBy = new Map<string, Money>();
  for (const x of input.paid ?? []) paidBy.set(x.key, (paidBy.get(x.key) ?? Money.zero()).add(Money.of(x.amount)));
  const installments: Installment[] = dates.map((d) => {
    const paid = paidBy.get(d.key) ?? Money.zero();
    const pending = each.sub(paid);
    return { key: d.key, nominal: d.nominal, due: d.legal, amount: each.toFixed(2), paid: paid.toFixed(2), pending: (pending.isPositive() ? pending : Money.zero()).toFixed(2) };
  });

  return {
    engine: PARTIAL_PAYMENTS_ENGINE_VERSION,
    fiscalYear: y,
    computable: true,
    provisional: reasons.length > 0,
    provisionalReasons: reasons,
    parameters: used,
    basis,
    priorYearTax: prev ? Money.of(prev.tax!).toFixed(2) : null,
    averageTax: average ? average.round(2).toFixed(2) : null,
    averageOver: filed.map((r) => r.year),
    base: base.round(2).toFixed(2),
    share,
    installments,
    total: Money.sum(installments.map((i) => Money.of(i.amount))).toFixed(2),
    exceptions,
    trace,
  };
}

// ------------------------------------------------------------------ bases

/** A filed annual return's figures as captured from TRIBU-CR/ATV (renta_prior_returns). */
export interface CapturedReturnTax {
  year: number;
  /** Impuesto según tarifa (before credits). */
  grossTax: string;
  familyCredits: string;
  capturedAt: string;
  ref: string;
}

/**
 * Tax of each of the three years before `fiscalYear` for the art. 22 a) base,
 * best source first: a figure typed for Y-1 on the spot (`manualPrior`), then
 * the latest captured return (tax after family credits, i.e. before pagos
 * parciales and retentions [verify: docs/tax/PAGOS_PARCIALES.md §6.2]), then
 * the amount recorded when the obligation was filed.
 */
export function priorYearsForBase(
  fiscalYear: number,
  sources: { manualPrior?: string | null; returns: readonly CapturedReturnTax[]; filed: readonly { year: number; amount: string | null; ref: string }[] },
): PriorYearTax[] {
  return [1, 2, 3].map((k) => {
    const year = fiscalYear - k;
    if (k === 1 && sources.manualPrior) return { year, tax: Money.of(sources.manualPrior).toFixed(2), ref: "manual:impuesto-anterior" };
    const ret = sources.returns.filter((r) => r.year === year).sort((a, b) => b.capturedAt.localeCompare(a.capturedAt))[0];
    if (ret) {
      const tax = Money.of(ret.grossTax).sub(Money.of(ret.familyCredits));
      return { year, tax: (tax.isNegative() ? Money.zero() : tax).toFixed(2), ref: `return:${ret.ref}` };
    }
    const f = sources.filed.find((x) => x.year === year && x.amount !== null);
    return { year, tax: f ? Money.of(f.amount!).toFixed(2) : null, ref: f?.ref ?? `renta:${year}` };
  });
}

// --------------------------------------------------------- reconciliation

/** A generated PAGOS_PARCIALES obligation of the year (period key "Y:Pn"). */
export interface InstallmentObligation {
  key: string;
  obligationId: string;
  dueDate: LocalDate;
  /** Latest amount captured from the Cuenta Integral Tributaria (record_official_amount), if any. */
  official: string | null;
  paid: string;
}

export type RequestStatus = "DRAFT" | "FILED" | "APPROVED" | "PARTIALLY_APPROVED" | "DENIED" | "WITHDRAWN";

/** A request to eliminate or reduce installments (Ley 7092 art. 22, last paragraph). */
export interface InstallmentRequest {
  id: string;
  kind: "ELIMINATION" | "REDUCTION";
  status: RequestStatus;
  installmentKeys: string[];
  requestedInstallmentAmount: string | null;
  approvedInstallmentAmount: string | null;
  filedOn: LocalDate | null;
}

export type InstallmentStatus = "PAID" | "PARTIALLY_PAID" | "PENDING" | "OVERDUE" | "NOT_DUE" | "NO_AMOUNT";

export type InstallmentFlagCode = "OFFICIAL_MISSING" | "OFFICIAL_DIFFERS" | "REQUEST_PENDING" | "REQUEST_LATE" | "OVERPAID";

export interface InstallmentRecon {
  key: string;
  obligationId: string | null;
  dueDate: LocalDate | null;
  /** Despejo's computed installment (art. 22). */
  expected: string | null;
  /** Hacienda's amount (Cuenta Integral Tributaria). */
  official: string | null;
  /** Amount granted by an approved request, when one applies. */
  approved: string | null;
  /** The amount that counts: official, else an approved request, else the computed one. */
  amount: string | null;
  source: "OFFICIAL" | "REQUEST" | "EXPECTED" | null;
  paid: string;
  pending: string | null;
  status: InstallmentStatus;
  flags: { code: InstallmentFlagCode; message: string }[];
}

export interface PartialPaymentsReconciliation {
  engine: string;
  fiscalYear: number;
  rows: InstallmentRecon[];
  totals: { expected: string; official: string | null; amount: string | null; paid: string; pending: string };
  /** Pagos parciales paid for the year vs the amount credited in that year's annual return (art. 22 c). */
  credit: { paid: string; credited: string | null; difference: string | null; status: "MATCH" | "MISMATCH" | "NO_RETURN"; ref: string | null };
  /** Requests whose filing date was on or after the due date of an installment they target. */
  lateRequests: { id: string; keys: string[] }[];
  ok: boolean;
}

const RESOLVED_OK = new Set<RequestStatus>(["APPROVED", "PARTIALLY_APPROVED"]);

/**
 * Expected (computed) vs official (Cuenta Integral Tributaria) vs paid, per
 * installment, and the year's total paid vs the credit taken in the annual
 * return (art. 22 c). A request only changes an installment if it was filed
 * BEFORE that installment's due date ("antes de la fecha de su vencimiento",
 * art. 22, last paragraph).
 */
export function reconcilePartialPayments(input: {
  fiscalYear: number;
  asOf: LocalDate;
  expected: PartialPaymentsResult | null;
  installments: readonly InstallmentObligation[];
  requests: readonly InstallmentRequest[];
  /** Pagos parciales credited in the filed annual return of the same year, when it was captured. */
  creditedInReturn?: { amount: string; ref: string } | null;
}): PartialPaymentsReconciliation {
  const expectedBy = new Map((input.expected?.computable ? input.expected.installments : []).map((i) => [i.key, i]));
  const keys = [...new Set([...input.installments.map((i) => i.key), ...expectedBy.keys()])].sort();
  const lateRequests: PartialPaymentsReconciliation["lateRequests"] = [];
  const rows: InstallmentRecon[] = keys.map((key) => {
    const ob = input.installments.find((i) => i.key === key) ?? null;
    const exp = expectedBy.get(key) ?? null;
    const due = ob?.dueDate ?? exp?.due ?? null;
    const flags: InstallmentRecon["flags"] = [];
    const official = ob && ob.official !== null ? Money.of(ob.official) : null;
    const expected = exp ? Money.of(exp.amount) : null;
    const paid = ob ? Money.of(ob.paid) : exp ? Money.of(exp.paid) : Money.zero();

    let approved: Money | null = null;
    for (const r of input.requests) {
      if (!r.installmentKeys.includes(key) || r.status === "DRAFT" || r.status === "WITHDRAWN") continue;
      if (r.filedOn !== null && due !== null && compareDates(r.filedOn, due) >= 0) {
        const hit = lateRequests.find((l) => l.id === r.id);
        if (hit) hit.keys.push(key);
        else lateRequests.push({ id: r.id, keys: [key] });
        flags.push({ code: "REQUEST_LATE", message: `La solicitud se presentó el ${r.filedOn}, no antes del vencimiento (${due}): no aplica a esta cuota (art. 22)` });
        continue;
      }
      if (r.status === "FILED") {
        flags.push({ code: "REQUEST_PENDING", message: "Solicitud presentada y sin resolver: si se deniega, la cuota se debe con intereses" });
      } else if (RESOLVED_OK.has(r.status)) {
        approved = Money.of(r.approvedInstallmentAmount ?? (r.kind === "ELIMINATION" ? "0" : (r.requestedInstallmentAmount ?? "0")));
      }
    }

    const amount = official ?? approved ?? expected;
    const source: InstallmentRecon["source"] = official ? "OFFICIAL" : approved ? "REQUEST" : expected ? "EXPECTED" : null;
    if (!official) flags.push({ code: "OFFICIAL_MISSING", message: "Falta el monto de la Cuenta Integral Tributaria (el de Hacienda manda)" });
    const reference = approved ?? expected;
    if (official && reference && !official.round(2).eq(reference.round(2))) {
      flags.push({ code: "OFFICIAL_DIFFERS", message: `Hacienda cobra ${official.toFixed(2)}; ${approved ? "la resolución" : "el cálculo"} da ${reference.toFixed(2)}` });
    }

    let status: InstallmentStatus;
    let pending: Money | null = null;
    if (!amount) {
      status = "NO_AMOUNT";
    } else {
      pending = amount.sub(paid);
      if (pending.isNegative()) {
        flags.push({ code: "OVERPAID", message: `Pagado ${paid.sub(amount).toFixed(2)} de más` });
        pending = Money.zero();
      }
      if (amount.isZero() && paid.isZero()) status = "NOT_DUE";
      else if (pending.isZero()) status = "PAID";
      else if (due !== null && compareDates(due, input.asOf) < 0) status = "OVERDUE";
      else status = paid.isPositive() ? "PARTIALLY_PAID" : "PENDING";
    }
    return {
      key,
      obligationId: ob?.obligationId ?? null,
      dueDate: due,
      expected: expected ? expected.toFixed(2) : null,
      official: official ? official.toFixed(2) : null,
      approved: approved ? approved.toFixed(2) : null,
      amount: amount ? amount.toFixed(2) : null,
      source,
      paid: paid.toFixed(2),
      pending: pending ? pending.toFixed(2) : null,
      status,
      flags,
    };
  });

  const sum = (xs: (string | null)[]) => Money.sum(xs.filter((x): x is string => x !== null).map((x) => Money.of(x)));
  const paid = sum(rows.map((r) => r.paid));
  const allOfficial = rows.length > 0 && rows.every((r) => r.official !== null);
  const allAmounts = rows.length > 0 && rows.every((r) => r.amount !== null);
  const credited = input.creditedInReturn ? Money.of(input.creditedInReturn.amount) : null;
  const difference = credited ? credited.sub(paid) : null;
  const credit: PartialPaymentsReconciliation["credit"] = {
    paid: paid.toFixed(2),
    credited: credited ? credited.toFixed(2) : null,
    difference: difference ? difference.toFixed(2) : null,
    status: !difference ? "NO_RETURN" : difference.round(2).isZero() ? "MATCH" : "MISMATCH",
    ref: input.creditedInReturn?.ref ?? null,
  };
  const settled = (r: InstallmentRecon) =>
    r.status === "PAID" || r.status === "NOT_DUE" || ((r.status === "PENDING" || r.status === "PARTIALLY_PAID") && r.dueDate !== null && compareDates(r.dueDate, input.asOf) >= 0);
  const ok =
    rows.every(settled) &&
    rows.every((r) => !r.flags.some((f) => f.code === "OFFICIAL_DIFFERS" || f.code === "REQUEST_LATE" || f.code === "OVERPAID")) &&
    credit.status !== "MISMATCH";
  return {
    engine: PARTIAL_PAYMENTS_ENGINE_VERSION,
    fiscalYear: input.fiscalYear,
    rows,
    totals: {
      expected: sum(rows.map((r) => r.expected)).toFixed(2),
      official: allOfficial ? sum(rows.map((r) => r.official)).toFixed(2) : null,
      amount: allAmounts ? sum(rows.map((r) => r.amount)).toFixed(2) : null,
      paid: paid.toFixed(2),
      pending: sum(rows.map((r) => r.pending)).toFixed(2),
    },
    credit,
    lateRequests,
    ok,
  };
}

/** Percentage change of `current` against `prior`, one decimal ("12.5"); null when prior is zero. */
export function pctChange(current: string, prior: string): string | null {
  const p = dec(prior);
  if (p.isZero()) return null;
  return dec(current).minus(p).dividedBy(p.abs()).times(100).toDecimalPlaces(1).toFixed(1);
}

